import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { moveLibraryItemToWorkspace } from "@/lib/library";
import type { ReviewItem } from "./useDowngradeReviewData";
import type { Workspace } from "@/hooks/useWorkspace";

const labelFor = (w: { workspace_label: string | null; display_name: string | null }) =>
  w.workspace_label || w.display_name || "Workspace";

type ExcerptRow = { id: string };

type Args<E extends ExcerptRow> = {
  items: ReviewItem[];
  setItems: React.Dispatch<React.SetStateAction<ReviewItem[]>>;
  excerpts: E[];
  setExcerpts: React.Dispatch<React.SetStateAction<E[]>>;
  reload: () => void;
  survivingWorkspaces: Workspace[];
  doomedWorkspaceIds: string[];
  refreshArchives: () => Promise<unknown> | void;
  refreshSub: () => Promise<unknown> | void;
  refreshWs: () => Promise<unknown> | void;
};

/** Owns all mutation handlers for the Downgrade Review page (item moves,
 *  deletes, bulk operations, and the workspace purge action). */
export function useDowngradeActions<E extends ExcerptRow>(args: Args<E>) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedExcerpts, setSelectedExcerpts] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [bulkTarget, setBulkTarget] = useState<string>("");
  const [purging, setPurging] = useState(false);

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === args.items.length) setSelected(new Set());
    else setSelected(new Set(args.items.map((i) => i.id)));
  };

  const toggleExcerpt = (id: string) =>
    setSelectedExcerpts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllExcerpts = () => {
    if (selectedExcerpts.size === args.excerpts.length) setSelectedExcerpts(new Set());
    else setSelectedExcerpts(new Set(args.excerpts.map((e) => e.id)));
  };

  const moveOne = async (item: ReviewItem, targetWorkspaceId: string) => {
    setBusy(true);
    try {
      await moveLibraryItemToWorkspace(item.id, item.workspace_id, targetWorkspaceId);
      toast.success("Moved");
      args.setItems((prev) => prev.filter((i) => i.id !== item.id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const deleteOne = async (item: ReviewItem) => {
    if (!confirm(`Delete "${item.display_title || item.title}" now? This cannot be undone.`)) return;
    setBusy(true);
    const { error } = await supabase.from("library_items").delete().eq("id", item.id);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    args.setItems((prev) => prev.filter((i) => i.id !== item.id));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(item.id);
      return next;
    });
  };

  const bulkMove = async () => {
    if (!bulkTarget || selected.size === 0) return;
    const target = args.survivingWorkspaces.find((w) => w.id === bulkTarget);
    if (!target) return;
    if (!confirm(`Move ${selected.size} item${selected.size === 1 ? "" : "s"} to "${labelFor(target)}"?`)) return;
    setBusy(true);
    const targets = args.items.filter((i) => selected.has(i.id));
    let success = 0;
    let failed = 0;
    for (const it of targets) {
      try {
        await moveLibraryItemToWorkspace(it.id, it.workspace_id, bulkTarget);
        success += 1;
      } catch {
        failed += 1;
      }
    }
    setBusy(false);
    if (success) toast.success(`Moved ${success}`);
    if (failed) toast.error(`${failed} item${failed === 1 ? "" : "s"} failed to move`);
    setSelected(new Set());
    args.reload();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Permanently delete ${selected.size} item${selected.size === 1 ? "" : "s"} now? This cannot be undone.`)) return;
    setBusy(true);
    const ids = Array.from(selected);
    const { error } = await supabase.from("library_items").delete().in("id", ids);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(`Deleted ${ids.length}`);
    setSelected(new Set());
    args.reload();
  };

  const deleteOneExcerpt = async (id: string) => {
    if (!confirm("Delete this excerpt now? This cannot be undone.")) return;
    setBusy(true);
    const { error } = await supabase.from("excerpts").delete().eq("id", id);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    args.setExcerpts((prev) => prev.filter((e) => e.id !== id));
    setSelectedExcerpts((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const bulkDeleteExcerpts = async () => {
    if (selectedExcerpts.size === 0) return;
    if (!confirm(`Permanently delete ${selectedExcerpts.size} excerpt${selectedExcerpts.size === 1 ? "" : "s"} now? This cannot be undone.`)) return;
    setBusy(true);
    const ids = Array.from(selectedExcerpts);
    const { error } = await supabase.from("excerpts").delete().in("id", ids);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(`Deleted ${ids.length}`);
    setSelectedExcerpts(new Set());
    args.reload();
  };

  const purgeNow = async () => {
    if (args.items.length > 0) {
      toast.error("Triage at-risk library items first.");
      return;
    }
    if (
      !confirm(
        `Permanently delete ${args.doomedWorkspaceIds.length} workspace${args.doomedWorkspaceIds.length === 1 ? "" : "s"} now? This cannot be undone and will not wait for the cutover date.`,
      )
    ) {
      return;
    }
    setPurging(true);
    let success = 0;
    let failed = 0;
    for (const wsId of args.doomedWorkspaceIds) {
      try {
        const { error } = await supabase.functions.invoke("delete-workspace", {
          body: { workspaceId: wsId },
        });
        if (error) throw new Error(error.message);
        success += 1;
      } catch (e) {
        console.warn("delete-workspace failed", wsId, e);
        failed += 1;
      }
    }
    setPurging(false);
    if (success) toast.success(`Deleted ${success} workspace${success === 1 ? "" : "s"}`);
    if (failed) toast.error(`${failed} workspace${failed === 1 ? "" : "s"} failed to delete`);
    await Promise.all([args.refreshArchives(), args.refreshSub(), args.refreshWs()]);
    if (failed === 0) navigate("/settings");
  };

  return {
    selected,
    selectedExcerpts,
    busy,
    bulkTarget,
    setBulkTarget,
    purging,
    toggleOne,
    toggleAll,
    toggleExcerpt,
    toggleAllExcerpts,
    moveOne,
    deleteOne,
    bulkMove,
    bulkDelete,
    deleteOneExcerpt,
    bulkDeleteExcerpts,
    purgeNow,
  };
}
